import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SearchResult {
  id: string;
  type: 'lead' | 'contact' | 'case' | 'deal';
  name: string;
  email?: string;
  phone?: string;
  status?: string;
  subtitle?: string;
  stage?: string;
  amount?: number;
}

export const useGlobalSearch = (query: string) => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const searchData = async () => {
      if (!query || query.length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        // Use RPC functions for accent-insensitive search
        const [leadsResult, contactsResult, dealsResult, casesResult] = await Promise.all([
          // Search leads using RPC
          supabase.rpc('search_leads_unaccent', { 
            search_term: query.trim(), 
            max_results: 5 
          }),
          // Search contacts using RPC
          supabase.rpc('search_contacts_unaccent', { 
            search_term: query.trim(), 
            max_results: 5 
          }),
          // Search deals using RPC
          supabase.rpc('search_deals_unaccent', { 
            search_term: query.trim(), 
            max_results: 5 
          }),
        // Search cases using RPC
        supabase.rpc('search_cases_unaccent', { 
          search_term: query.trim(), 
          max_results: 5 
        })
        ]);

        const leads = leadsResult.data || [];
        const contacts = contactsResult.data || [];
        const deals = dealsResult.data || [];
        const cases = casesResult.data || [];

        const allResults: SearchResult[] = [
          ...leads.map((lead: any) => ({
            id: lead.id,
            type: 'lead' as const,
            name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed Lead',
            email: lead.primary_email,
            phone: lead.primary_phone,
            status: lead.lifecycle_stage,
            subtitle: lead.primary_email || lead.primary_phone,
          })),
          ...contacts.map((contact: any) => ({
            id: contact.id,
            type: 'contact' as const,
            name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed Contact',
            email: contact.primary_email,
            phone: contact.primary_phone,
            subtitle: contact.primary_email || contact.primary_phone,
          })),
          ...deals.map((deal: any) => ({
            id: deal.id,
            type: 'deal' as const,
            name: deal.name || 'Unnamed Deal',
            stage: deal.stage,
            amount: deal.total_amount,
            subtitle: `${deal.stage || 'Unknown'} • ${deal.currency || '$'}${deal.total_amount?.toLocaleString() || '0'}`,
          })),
        ...cases.map((caseItem: any) => ({
          id: caseItem.id,
          type: 'case' as const,
          name: caseItem.catalog_name || 'Processo',
          status: caseItem.status,
          subtitle: caseItem.contact_name || caseItem.id,
        })),
        ];

        setResults(allResults);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchData, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  return { results, loading };
};
